import React, { useEffect, useRef, useState } from "react";
import { Box, Container, Grid, Typography, Link } from "@mui/material";
import Slide from "@mui/material/Slide";

type AnimationType = "fade-up" | "zoom-in";

interface AnimatedProps {
  animation: AnimationType;
  delay?: number;
  children: React.ReactNode;
}

function useInView(
  delay: number = 0
): [React.RefObject<HTMLDivElement | null>, boolean] {
  const ref = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState<boolean>(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setTimeout(() => {
            setVisible(true);
          }, delay);
          observer.unobserve(el);
        }
      },
      { threshold: 0.1 }
    );

    observer.observe(el);

    return () => {
      observer.disconnect();
    };
  }, [delay]);

  return [ref, visible];
}

function Animated({ animation, delay = 0, children }: AnimatedProps) {
  const [ref, visible] = useInView(delay);
  const baseTransition = "opacity 0.8s ease-out, transform 0.8s ease-out";
  // Initialize with a default transform property
  let initialStyle: React.CSSProperties = {
    opacity: 0,
    transition: baseTransition,
    transform: "none",
  };

  if (animation === "fade-up") {
    initialStyle = { ...initialStyle, transform: "translateY(20px)" };
  } else if (animation === "zoom-in") {
    initialStyle = { ...initialStyle, transform: "scale(0.9)" };
  }

  const visibleStyle: React.CSSProperties = {
    opacity: 1,
    transform: "none",
    transition: baseTransition,
  };

  return (
    <Box ref={ref} sx={visible ? visibleStyle : initialStyle}>
      {children}
    </Box>
  );
}

export default function Dieticians() {
  return (
    <Box>
      {/* Hero Section */}
      <Box sx={{ backgroundColor: "#fff", py: 8 }}>
        <Container maxWidth="lg">
          <Grid container spacing={4} alignItems="center">
            {/* Text Column */}
            <Grid item xs={12} md={6}>
              <Animated animation="fade-up">
                <Typography
                  variant="h3"
                  fontWeight="800"
                  component="h1"
                  gutterBottom
                  align="left"
                  sx={{ whiteSpace: "pre-wrap" }}
                >
                  Empower Your Clients with
                  {"\n"}Dietitians Dashboard
                </Typography>
              </Animated>
              <Animated animation="fade-up" delay={200}>
                <Typography
                  variant="subtitle2"
                  component="p"
                  gutterBottom
                  align="left"
                  fontWeight="ExtraBold"
                  lineHeight={1.65}
                  maxWidth={500}
                >
                  Our platform gives you the tools to provide free fitness
                  tracking, review food data from our partners portal, and gain
                  insights from client workouts.
                </Typography>
              </Animated>
            </Grid>
            {/* Hero Image */}
            <Grid item xs={12} md={6}>
              <Animated animation="fade-up" delay={400}>
                <Box
                  component="img"
                  src="/images/dietitian/desktop.png"
                  alt="Dietitians Dashboard"
                  sx={{ width: "100%", height: "auto", borderRadius: 5 }}
                />
              </Animated>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Features Section */}
      <Box sx={{ backgroundColor: "#f9f9f9", py: 8 }}>
        <Container maxWidth="lg">
          {/* Feature 1: Free Fitness Tracking */}
          <Box
            sx={{
              backgroundColor: "#72b09a",
              py: 4,
              px: 6,
              borderRadius: 4,
              mb: 4,
            }}
          >
            <Grid container spacing={4} alignItems="center">
              <Grid item xs={12} md={6}>
                <Box sx={{ color: "white", maxWidth: 500 }}>
                  <Typography
                    variant="h4"
                    sx={{ fontWeight: 700 }}
                    gutterBottom
                  >
                    Free Fitness Tracking
                  </Typography>
                  <Typography variant="body1">
                    Offer your clients a free, intuitive way to track their
                    workouts and overall fitness, all in one easy-to-use app.
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={12} md={6}>
                <Box
                  component="img"
                  src="/images/dietitian/appstore.png"
                  alt="Fitness Tracking"
                  sx={{ width: "50%", height: "auto", borderRadius: 5 }}
                />
              </Grid>
            </Grid>
          </Box>

          {/* Feature 2: Review Food Data */}
          <Box
            sx={{
              backgroundColor: "#c9c9c9",
              py: 4,
              px: 6,
              borderRadius: 4,
              mb: 4,
            }}
          >
            <Grid container spacing={4} alignItems="center">
              <Grid item xs={12} md={6}>
                <Box
                  component="img"
                  src="/images/dietitian/desktop.png"
                  alt="Food Data"
                  sx={{ width: "100%", height: "auto", borderRadius: 5 }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <Box sx={{ color: "white", maxWidth: 500 }}>
                  <Typography
                    variant="h4"
                    sx={{ fontWeight: 700 }}
                    gutterBottom
                  >
                    Review Food Data
                  </Typography>
                  <Typography variant="body1">
                    Access detailed food data directly from our partners’
                    portal, giving you real-time insights into your clients’
                    nutritional intake.
                  </Typography>
                </Box>
              </Grid>
            </Grid>
          </Box>

          {/* Feature 3: Client Workout Insights */}
          <Box
            sx={{
              backgroundColor: "#72b09a",
              py: 4,
              px: 6,
              borderRadius: 4,
              mb: 4,
            }}
          >
            <Grid container spacing={4} alignItems="center">
              <Grid item xs={12} md={6}>
                <Box sx={{ color: "white", maxWidth: 500 }}>
                  <Typography
                    variant="h4"
                    sx={{ fontWeight: 700 }}
                    gutterBottom
                  >
                    Client Workout Insights
                  </Typography>
                  <Typography variant="body1">
                    Review client workout data to better understand their
                    lifestyle, so you can offer more tailored guidance and
                    nutrition advice.
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={12} md={6}>
                <Box
                  component="img"
                  src="/images/dietitian/exercise.png"
                  alt="Workout Insights"
                  sx={{ width: "100%", height: "auto", borderRadius: 5 }}
                />
              </Grid>
            </Grid>
          </Box>

          {/* Feature 4: Client Mode for Food Anxiety */}
          <Box
            sx={{
              backgroundColor: "#c9c9c9",
              py: 4,
              px: 6,
              borderRadius: 4,
              mb: 4,
            }}
          >
            <Grid container spacing={4} alignItems="center">
              <Grid item xs={12} md={6}>
                <Box
                  component="img"
                  src="/images/dietitian/foodanxiety.png"
                  alt="Client Mode"
                  sx={{ width: "50%", height: "auto", borderRadius: 5 }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <Box sx={{ color: "white", maxWidth: 500 }}>
                  <Typography
                    variant="h4"
                    sx={{ fontWeight: 700 }}
                    gutterBottom
                  >
                    Client Mode for Food Anxiety
                  </Typography>
                  <Typography variant="body1">
                    For clients with food anxiety, enable "client mode" to hide
                    calorie and macro numbers. Instead, clients can simply log
                    how full or hungry they felt and the percentage of food
                    consumed.
                  </Typography>
                </Box>
              </Grid>
            </Grid>
          </Box>

          {/* Feature 5: Coming Soon - Macro Adjustments */}
          <Box
            sx={{
              backgroundColor: "#72b09a",
              py: 4,
              px: 6,
              borderRadius: 4,
              mb: 4,
            }}
          >
            <Grid container spacing={4} alignItems="center">
              <Grid item xs={12} md={6}>
                <Box sx={{ color: "white", maxWidth: 500 }}>
                  <Typography
                    variant="h4"
                    sx={{ fontWeight: 700 }}
                    gutterBottom
                  >
                    Coming Soon: Macro Adjustments
                  </Typography>
                  <Typography variant="body1">
                    Soon, dietitians will be able to adjust user calories and
                    macros directly, allowing even more personalized nutrition
                    planning.
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={12} md={6}>
                <Box
                  component="img"
                  src="/images/dietitian/womantrackfood.jpg"
                  alt="Macro Adjustments"
                  sx={{ width: "100%", height: "auto", borderRadius: 5 }}
                />
              </Grid>
            </Grid>
          </Box>
        </Container>
      </Box>

      {/* Call-to-Action Section */}
      <Box sx={{ backgroundColor: "#f3f4f6", py: 8, textAlign: "center" }}>
        <Container maxWidth="md">
          <Typography
            variant="h1"
            component="h1"
            gutterBottom
            sx={{ fontWeight: 800, fontSize: 96 }}
          >
            Enhance Client Success.
          </Typography>
          <Typography variant="h6" component="p" gutterBottom>
            Join a community of dedicated dietitians using our platform to
            transform client health.
          </Typography>
          <Animated animation="zoom-in" delay={200}>
            <Link
              href="https://apps.apple.com/us/app/avro-workout-diet-tracker/id6739968450"
              target="_blank"
              rel="noopener noreferrer"
              sx={{
                display: "inline-block",
                mt: 4,
                backgroundColor: "#000",
                p: 2,
                borderRadius: 3,
                width: "fit-content",
              }}
            >
              <Typography
                sx={{
                  color: "white",
                  textAlign: "center",
                  fontWeight: "bold",
                  fontSize: 20,
                }}
              >
                Get Started
              </Typography>
            </Link>
          </Animated>
        </Container>
      </Box>

      {/* Footer */}
      <Box sx={{ backgroundColor: "#ffffff", py: 2, textAlign: "center" }}>
        <Typography variant="body2" color="text.secondary">
          &copy; {new Date().getFullYear()} Avro. All rights reserved.
        </Typography>
      </Box>
    </Box>
  );
}
